/*
Author: Chathuraka Mallawa Arachchi
Date: 10/12/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import {
    DropDownOption,
    DropDownOptionList,
} from '@/components/survey/SurveyModel/types/nodeTypes'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'

export interface IformInputState {
    excludedSelectOptions: DropDownOptionList | null
}

const initialState: IformInputState = {
    excludedSelectOptions: null,
}
//DropDownOption
const formInputSlice = createSlice({
    name: 'formInput',
    initialState,
    reducers: {
        updateExcludeOptions: (
            state,
            action: PayloadAction<{
                option: DropDownOption
                fieldName: string
            }>
        ) => {
            const { option, fieldName } = action.payload
            if (
                state.excludedSelectOptions &&
                state.excludedSelectOptions[fieldName]
            ) {
                if (
                    !state.excludedSelectOptions[fieldName].find(
                        (thisOption) => thisOption.label === option.label
                    )
                ) {
                    state.excludedSelectOptions[fieldName].push(option)
                }
            } else {
                const { option, fieldName } = action.payload
                if (state.excludedSelectOptions) {
                    state.excludedSelectOptions[`${fieldName}`] = [option]
                } else {
                    state.excludedSelectOptions = {}
                    state.excludedSelectOptions[`${fieldName}`] = [option]
                }
            }
        },

        updateExcludeOptionsList: (
            state,
            action: PayloadAction<{
                options: DropDownOption[]
                fieldName: string
            }>
        ) => {
            const { options, fieldName } = action.payload
            if (
                state.excludedSelectOptions &&
                state.excludedSelectOptions[fieldName]
            ) {
                state.excludedSelectOptions[fieldName] = options
            } else {
                const { options, fieldName } = action.payload
                if (state.excludedSelectOptions) {
                    state.excludedSelectOptions[`${fieldName}`] = options
                } else {
                    state.excludedSelectOptions = {}
                    state.excludedSelectOptions[`${fieldName}`] = options
                }
            }
        },
        removeExcludeOptionsList: (
            state,
            action: PayloadAction<{ fieldName: string }> // fieldName
        ) => {
            const { fieldName } = action.payload
            if (
                state.excludedSelectOptions &&
                state.excludedSelectOptions[fieldName]
            ) {
                delete state.excludedSelectOptions[fieldName]

                // Optional: If excludedSelectOptions becomes empty, you can set it to undefined or {}
                if (Object.keys(state.excludedSelectOptions).length === 0) {
                    state.excludedSelectOptions = {}
                }
            }
        },
        resetOptionsList: () => initialState,
        removeExcludeOption: (
            state,
            action: PayloadAction<{
                option: DropDownOption
                fieldName: string
            }>
        ) => {
            const { option, fieldName } = action.payload

            if (
                state.excludedSelectOptions &&
                state.excludedSelectOptions?.[fieldName]
            ) {
                const optionsForField = state.excludedSelectOptions?.[fieldName]
                state.excludedSelectOptions[fieldName] = optionsForField.filter(
                    (thisOption) => thisOption.label !== option.label
                )
            }
        },
    },
})

export const {
    updateExcludeOptions,
    removeExcludeOption,
    resetOptionsList,
    updateExcludeOptionsList,
    removeExcludeOptionsList,
} = formInputSlice.actions

export const useUpdateExcludeData = () => {
    const dispatch = useDispatch()

    const updateExcludeData = (option: DropDownOption, fieldName: string) => {
        dispatch(updateExcludeOptions({ option: option, fieldName: fieldName }))
    }

    return updateExcludeData
}

export const useRemoveExcludeData = () => {
    const dispatch = useDispatch()

    const removeExcludeData = (option: DropDownOption, fieldName: string) => {
        dispatch(removeExcludeOption({ option: option, fieldName: fieldName }))
    }

    return removeExcludeData
}

export const useUpdateExcludeOptionsList = () => {
    const dispatch = useDispatch()

    const addExcludeOptionsList = (
        options: DropDownOption[],
        fieldName: string
    ) => {
        dispatch(
            updateExcludeOptionsList({ options: options, fieldName: fieldName })
        )
    }

    return addExcludeOptionsList
}
export const useRemoveExcludeOptionsList = () => {
    const dispatch = useDispatch()

    const updateRemoveOptionsList = (fieldName: string) => {
        dispatch(
            removeExcludeOptionsList({
                fieldName: fieldName,
            })
        )
    }

    return updateRemoveOptionsList
}

export const useResetExcludeOptionsList = () => {
    const dispatch = useDispatch()

    const resetExcludeOptionsList = () => {
        dispatch(resetOptionsList())
    }

    return resetExcludeOptionsList
}

export default formInputSlice.reducer
